/**
 * @kgc/service-worksheet - Worksheet Interfaces
 * Epic 17: Work Orders
 *
 * Munkalap (Work Order) típusok és interfészek
 */

/**
 * Munkalap státuszok - State machine
 *
 * Átmenetek:
 * FELVEVE → FOLYAMATBAN → KESZ → SZAMLAZANDO → LEZART
 *         ↘ VARHATO ↗
 *         ↘ TOROLVE (bármikor FELVEVE-ből)
 */
export enum WorksheetStatus {
  /** Felvéve - Új munkalap */
  FELVEVE = 'FELVEVE',
  /** Folyamatban - Szerelő dolgozik rajta */
  FOLYAMATBAN = 'FOLYAMATBAN',
  /** Várakozó - Alkatrészre vagy ügyfélre vár */
  VARHATO = 'VARHATO',
  /** Felfüggesztve - Plusz költség, ügyfél döntésre vár (SRV-FG-2-1) */
  FELFUGGESZTVE = 'FELFUGGESZTVE',
  /** Kész - Munka befejezve, számlázandó */
  KESZ = 'KESZ',
  /** Számlázandó - Számla készítés alatt */
  SZAMLAZANDO = 'SZAMLAZANDO',
  /** Lezárt - Minden kész, archivált */
  LEZART = 'LEZART',
  /** Törölve - Sztornózott */
  TOROLVE = 'TOROLVE',
}

/**
 * Munkalap típusok
 */
export enum WorksheetType {
  /** Fizetős javítás */
  FIZETOS = 'FIZETOS',
  /** Garanciális javítás */
  GARANCIALIS = 'GARANCIALIS',
  /** Bérléssel kapcsolatos szerviz */
  BERLESI = 'BERLESI',
  /** Karbantartás */
  KARBANTARTAS = 'KARBANTARTAS',
  /** Szerződéses partner javítás */
  SZERZODESES = 'SZERZODESES',
}

/**
 * Munkalap szám prefix meghatározása típus alapján — **FK-SZE-5** (kliens-séma,
 * véglegesítve 2026-03-06, forrás `ERP-TESZT (29)/(30).md`).
 *
 * - FIZETOS → ML-J (javítás)
 * - GARANCIALIS → ML-G (garanciális)
 * - SZERZODESES → ML-J-P (szerződéses partner)
 * - BERLESI → ML-BH (bérlésből visszahozott)
 * - KARBANTARTAS → ML-J ⚠️ lásd lent
 *
 * 🔴 **QA-106 — a korábbi séma ÜTKÖZÖTT a kliensével**: az `ML-S` a kódban „standard
 * (fizetős) szerviz"-t jelentett, a kliensnél viszont **bérgép/saját eszköz**-t. Aki a
 * kliens szótárával olvasott egy kódot, rossz kategóriát értett — és nem is látta.
 * Ezért az `ML-S`-t itt NEM adjuk vissza egyetlen ágon sem.
 *
 * ⚠️ **Két FK-SZE-5 kategóriának NINCS `WorksheetType` párja**, ezért nem
 * implementálható: `ML-J-S`/`ML-J-SOS` (sürgős, díj + 2× felár) és `ML-S`
 * (bérgép/saját eszköz). Új enum-érték kell hozzájuk — külön tétel.
 *
 * ⚠️ **A `KARBANTARTAS`-nak NINCS FK-SZE-5 kategóriája**, és a kliens-séma sem
 * nevezi meg. A `default` ágon `ML-J`-t kap — ez **feltevés, nem ratifikált döntés**:
 * azért ez, mert a korábbi `ML-S` default a kliens szótárában „bérgép"-et jelentene,
 * ami mérhetően rosszabb. 🔵 Javo/kliens-döntés tárgya.
 *
 * 📌 A meglévő kódok NEM íródnak át (🚦 kiosztás) — a migráció külön tétel.
 */
export function getWorksheetPrefix(type?: WorksheetType): string {
  switch (type) {
    case WorksheetType.GARANCIALIS:
      return 'ML-G';
    case WorksheetType.SZERZODESES:
      return 'ML-J-P';
    case WorksheetType.BERLESI:
      return 'ML-BH';
    case WorksheetType.FIZETOS:
    case WorksheetType.KARBANTARTAS:
    default:
      return 'ML-J';
  }
}

/**
 * Munkalap prioritások (FR91-FR93)
 */
export enum WorksheetPriority {
  /** Sürgős - Felár (+30%) (ERP-TESZT-11 döntés: 10%→30%) */
  SURGOS = 'SURGOS',
  /** Felár - Extra szolgáltatás */
  FELARAS = 'FELARAS',
  /** Garanciális - Prioritásos kezelés */
  GARANCIALIS = 'GARANCIALIS',
  /** Franchise partner - Kiemelt kezelés */
  FRANCHISE = 'FRANCHISE',
  /** Normál - Alapértelmezett */
  NORMAL = 'NORMAL',
}

/**
 * Munkalap entitás
 */
export interface IWorksheet {
  /** Egyedi azonosító (UUID) */
  id: string;
  /** Tenant azonosító (multi-tenancy) */
  tenantId: string;
  /** Munkalap szám (ML-YYYY-NNNN) */
  worksheetNumber: string;
  /** Munkalap típus */
  type: WorksheetType;
  /** Aktuális státusz */
  status: WorksheetStatus;
  /** Prioritás */
  priority: WorksheetPriority;
  /** Partner azonosító */
  partnerId: string;
  /** Gép megnevezés */
  deviceName: string;
  /** Gép sorozatszám */
  deviceSerialNumber?: string;
  /** Hiba leírás (ügyfél által) */
  faultDescription: string;
  /** Diagnosztika (szerelő által) */
  diagnosis?: string;
  /** Elvégzett munka leírás */
  workPerformed?: string;
  /** Belső megjegyzés (csak dolgozóknak) */
  internalNote?: string;
  /** Felelős szerelő ID */
  assignedToId?: string;
  /** Javítási költség limit (FR107) */
  costLimit?: number;
  /**
   * Q-T171: a bevizsgálási díj ki van-e fizetve. A payment-gate ebből dönt
   * (FELVEVE → FOLYAMATBAN a FIZETOS típusnál), és az inspection-fee-credit
   * ebből számol jóváírást a végszámlán.
   */
  inspectionFeePaid?: boolean;
  /** Becsült javítási költség (nettó, HUF) */
  estimatedCost?: number | null;
  /** Ajánlat szükséges-e (false = azonnali javítás) */
  requiresQuote?: boolean;
  /** Becsült befejezés dátum */
  estimatedCompletionDate?: Date;
  /** Felvétel dátuma */
  receivedAt: Date;
  /** Lezárás dátuma */
  completedAt?: Date;
  // Lifecycle timestamps (nagykör-1 contract-append, 2026-06-11 — read-seam a page11 Előzmény-timeline-hoz)
  /** Diagnózis elkészültének dátuma */
  diagnosedAt?: Date;
  /** Javítás megkezdésének dátuma */
  startedAt?: Date;
  /** Gép kiadásának dátuma (ügyfélnek átadva) */
  deliveredAt?: Date;
  /** Ügyfél értesítve (tárolási díj trigger, Story 58-10) */
  customerNotifiedAt?: Date;
  /** Adminisztratív lezárás dátuma (closedBy user által) */
  closedAt?: Date;
  /** QR kód URL (ügyfél státusz lekérdezés) */
  qrCode?: string;
  /** Ügyfél státusz token (publikus elérés) */
  customerStatusToken?: string;
  /** Kapcsolódó bérlés ID */
  rentalId?: string;
  /** Beadási batch azonosító (page10-B többgépes intake) */
  intakeBatchId?: string;
  /** Létrehozó user */
  createdBy: string;
  /** Létrehozás dátum */
  createdAt: Date;
  /** Módosítás dátum */
  updatedAt: Date;
}

/**
 * Munkalap tétel (alkatrész/munkadíj)
 */
export interface IWorksheetItem {
  /** Egyedi azonosító */
  id: string;
  /** Munkalap ID */
  worksheetId: string;
  /** Tenant ID */
  tenantId: string;
  /** Cikk ID (alkatrész esetén) */
  productId?: string;
  /** Tétel megnevezés */
  description: string;
  /** Mennyiség */
  quantity: number;
  /** Egységár (nettó, HUF) */
  unitPrice: number;
  /** ÁFA kulcs (%) */
  vatRate: number;
  /** Nettó összeg */
  netAmount: number;
  /** Bruttó összeg */
  grossAmount: number;
  /** Tétel típus */
  itemType: 'PART' | 'LABOR' | 'OTHER' | 'EXTERNAL_COST';
  /** Létrehozás dátum */
  createdAt: Date;
}

/**
 * Munkalap összesítő
 */
export interface IWorksheetSummary {
  /** Munkalap ID */
  worksheetId: string;
  /** Alkatrész költség (nettó) */
  partsNetAmount: number;
  /** Munkadíj (nettó) */
  laborNetAmount: number;
  /** Egyéb költség (nettó) */
  otherNetAmount: number;
  /** Alvállalkozói díj (nettó) */
  externalCostNetAmount: number;
  /** Teljes nettó összeg */
  totalNetAmount: number;
  /** Teljes bruttó összeg */
  totalGrossAmount: number;
  /** ÁFA összeg */
  vatAmount: number;
}

/**
 * Munkalap szám generálás eredménye
 */
export interface IWorksheetNumberResult {
  worksheetNumber: string;
  year: number;
  sequence: number;
}
